// Stage B1 — Sounds to words. Items (sounds/skills) + the 30 units, transcribed
// from the Word Engine Curriculum. Words are decoded on the fly by wordEngine.js
// and played through the shared phoneme library, so only text is stored here.

// --- Items: a sound/skill the child can learn. `code` maps to the phoneme
// library (audio key ph:<code>); skills/morphemes have no single-sound audio. ---
const G = (display, code, sound) => ({ id: display, kind: 'grapheme', display, letter_names: display.toUpperCase().split(''), code, sound })

export const ITEMS = {
  s: G('s', 's', '/s/'), a: G('a', 'a', '/a/'), t: G('t', 't', '/t/'), p: G('p', 'p', '/p/'),
  i: G('i', 'i', '/i/'), n: G('n', 'n', '/n/'), m: G('m', 'm', '/m/'), d: G('d', 'd', '/d/'),
  g: G('g', 'g', '/g/'), o: G('o', 'o', '/o/'), c: G('c', 'k', '/k/'), k: G('k', 'k', '/k/'),
  ck: G('ck', 'k', '/k/'), e: G('e', 'e', '/e/'), u: G('u', 'u', '/u/'), r: G('r', 'r', '/r/'),
  h: G('h', 'h', '/h/'), b: G('b', 'b', '/b/'), f: G('f', 'f', '/f/'), ff: G('ff', 'f', '/f/'),
  l: G('l', 'l', '/l/'), ll: G('ll', 'l', '/l/'), ss: G('ss', 's', '/s/'), j: G('j', 'j', '/j/'),
  v: G('v', 'v', '/v/'), w: G('w', 'w', '/w/'), x: G('x', 'ks', '/ks/'), y: G('y', 'y', '/y/'),
  z: G('z', 'z', '/z/'), zz: G('zz', 'z', '/z/'), qu: G('qu', 'kw', '/kw/'),
}

export const SKILLS = {
  'suffix-s': { id: 'suffix-s', kind: 'morpheme', display: '-s', sound: null, meaning: 'more than one' },
  'blend-final': { id: 'blend-final', kind: 'skill', display: 'final blends', meaning: '-mp -nd -nk -nt -st …' },
  'blend-initial': { id: 'blend-initial', kind: 'skill', display: 'start blends', meaning: 'st- sp- tr- fl- …' },
  'blend-both': { id: 'blend-both', kind: 'skill', display: 'blends review', meaning: 'CCVCC' },
}

export function itemById(id) { return ITEMS[id] || SKILLS[id] || null }

// --- Units ---
const U = (order, id, title, new_items, words, tricky = [], sentences = []) =>
  ({ id, stage: 'B1', order, title, new_items, words, tricky, sentences, mastery: { accuracy: 0.9, sessions: 2 } })

export const UNITS = [
  U(1, 'B1.01', '/s/ /a/ /t/', ['s', 'a', 't'], ['at', 'sat', 'tat']),
  U(2, 'B1.02', '/p/', ['p'], ['pat', 'tap', 'sap', 'pats', 'taps']),
  U(3, 'B1.03', '/i/', ['i'], ['it', 'sit', 'pit', 'tip', 'sip', 'pip', 'tips'], ['I', 'a']),
  U(4, 'B1.04', '/n/', ['n'], ['in', 'an', 'tin', 'pin', 'nip', 'nap', 'pan', 'snap'], ['is']),
  U(5, 'B1.05', 'Suffix -s', ['suffix-s'], ['pins', 'pans', 'naps', 'sips', 'tins']),
  U(6, 'B1.06', '/m/', ['m'], ['am', 'man', 'mat', 'map', 'mad', 'Sam', 'Tim'], ['the']),
  U(7, 'B1.07', '/d/', ['d'], ['dad', 'did', 'dip', 'sad', 'mad', 'and', 'dim'], ['and', 'to']),
  U(8, 'B1.08', '/g/', ['g'], ['gap', 'gas', 'dig', 'pig', 'tag', 'sag']),
  U(9, 'B1.09', '/o/', ['o'], ['on', 'top', 'pot', 'dog', 'not', 'got', 'mop', 'spot'], ['no', 'go'], ['The dog is on top.']),
  U(10, 'B1.10', '/k/ <c>', ['c'], ['cat', 'cot', 'cap', 'can', 'cod', 'cog'], ['of']),
  U(11, 'B1.11', '/k/ <k>', ['k'], ['kit', 'kid', 'kin', 'Kim']),
  U(12, 'B1.12', '/k/ <ck>', ['ck'], ['back', 'sack', 'kick', 'sock', 'dock', 'pick', 'tick']),
  U(13, 'B1.13', '/e/', ['e'], ['pet', 'ten', 'net', 'men', 'peg', 'get', 'set', 'sent'], ['he', 'she', 'me', 'we', 'be']),
  U(14, 'B1.14', '/u/', ['u'], ['up', 'cup', 'mud', 'sun', 'run', 'tuck', 'dug', 'gum']),
  U(15, 'B1.15', '/r/', ['r'], ['rat', 'rip', 'run', 'rug', 'rod', 'red', 'rock'], ['was']),
  U(16, 'B1.16', '/h/', ['h'], ['hat', 'hit', 'hop', 'hug', 'hen', 'hum'], [], ['The hen sat on a mat.']),
  U(17, 'B1.17', '/b/', ['b'], ['bat', 'bit', 'bus', 'bag', 'bed', 'bug', 'tub', 'rub']),
  U(18, 'B1.18', '/f/ <f> <ff>', ['f', 'ff'], ['fan', 'fit', 'fun', 'off', 'puff', 'huff', 'cuff'], ['for']),
  U(19, 'B1.19', '/l/ <l> <ll>', ['l', 'll'], ['lap', 'leg', 'log', 'lip', 'bell', 'hill', 'doll', 'fill']),
  U(20, 'B1.20', '/s/ <ss>', ['ss'], ['hiss', 'kiss', 'mess', 'boss', 'fuss', 'less']),
  U(21, 'B1.21', '/j/', ['j'], ['jam', 'jet', 'jog', 'jug', 'jab']),
  U(22, 'B1.22', '/v/', ['v'], ['van', 'vet', 'vat']),
  U(23, 'B1.23', '/w/', ['w'], ['wet', 'win', 'web', 'wag', 'wig']),
  U(24, 'B1.24', '/ks/ <x>', ['x'], ['fox', 'box', 'six', 'mix', 'wax']),
  U(25, 'B1.25', '/y/', ['y'], ['yes', 'yet', 'yap', 'yum'], ['you', 'my']),
  U(26, 'B1.26', '/z/ <z> <zz>', ['z', 'zz'], ['zip', 'zap', 'buzz', 'fizz', 'jazz'], ['do']),
  U(27, 'B1.27', '/kw/ <qu>', ['qu'], ['quit', 'quiz', 'quick'], ['has', 'his']),
  U(28, 'B1.28', 'Final blends (CVCC)', ['blend-final'], ['end', 'and', 'hand', 'milk', 'jump', 'help', 'lamp', 'best', 'gift', 'sink', 'bank'], ['have', 'give']),
  U(29, 'B1.29', 'Start blends (CCVC)', ['blend-initial'], ['stop', 'spin', 'trip', 'flag', 'drop', 'plan', 'grab', 'twin'], ['from', 'put']),
  U(30, 'B1.30', 'Blends review (CCVCC)', ['blend-both'], ['stamp', 'blend', 'twist', 'crust', 'print'], ['what', 'want']),
]

export const MAX_ORDER = UNITS.length
export const STAGES = { B1: 'Sounds to words' }
export const unitByOrder = (order) => UNITS.find((u) => u.order === order) || null

// All words in units strictly before `order` (the mastered/earlier pool) — used to
// pick spaced-review words. Returns a flat array of word strings, in unit order,
// not de-duplicated (words like 'and'/'run' recur across units).
export function earlierWords(order) {
  const out = []
  for (const u of UNITS) {
    if (u.order >= order) break
    for (const w of u.words) out.push(w)
  }
  return out
}
